import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { TPromoBannerBase } from '@onesource/schemas';
import { sanitizeRichHtml } from '@onesource/utils';
import { FC } from 'react';
import { MdChevronRight } from 'react-icons/md';

import { useGetByDevice } from '../hooks';
import { resolvePromoBannerBackground } from './promo-banner-colors';
import { getPromoBannerIconScheme } from './promo-banner-icon-scheme';
import { promoBannerRichTextSx } from './promo-banner-rich-text';

export interface PromoBannerDefaultProps {
  banner: TPromoBannerBase;
  /**
   * `row` (default) — badge, copy and CTA side by side. `column` — stacked
   * for a narrow slot (the community feed rail): the badge drops out and the
   * CTA sits under the copy, as in the feed design.
   */
  layout?: 'row' | 'column';
}

/**
 * Default promo banner layout.
 *
 * Coral-gradient card with a configurable icon badge (banner.iconScheme),
 * title, description and a pill CTA that links to the configured URL. Lays out
 * as a row on desktop and stacks on mobile.
 */
export const PromoBannerDefault: FC<PromoBannerDefaultProps> = ({ banner, layout = 'row' }) => {
  const isExternal = /^https?:\/\//i.test(banner.buttonUrl);
  const { isMobile } = useGetByDevice();
  const BadgeIcon = getPromoBannerIconScheme(banner.iconScheme);
  const isColumn = layout === 'column';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isColumn ? 'column' : { xs: 'row', sm: 'row' },
        alignItems: isColumn ? 'stretch' : { xs: 'flex-start', sm: 'center' },
        gap: isColumn ? 1.5 : { xs: 1, sm: 2 },
        p: isColumn ? 1.5 : { xs: 1, sm: 2.5 },
        borderRadius: { xs: '11px', md: '15px' },
        width: '100%',
        minHeight: isColumn ? 'auto' : { xs: '59px', md: '79px' },
        background: resolvePromoBannerBackground(banner.backgroundColor),
      }}
    >
      {!isColumn && (
        <Box
          sx={{
            flexShrink: 0,
            minWidth: { xs: '22px', md: '46px' },
            minHeight: { xs: '22px', md: '46px' },
            borderRadius: { xs: '6px', md: '12px' },
            bgcolor: '#FFF1EF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <BadgeIcon width={isMobile ? 14 : 29} height={isMobile ? 14 : 29} />
        </Box>
      )}

      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            ...promoBannerRichTextSx,
            color: theme => theme.palette.common.white,
            fontWeight: 700,
            fontSize: isColumn ? 14 : { xs: 12, md: 16 },
            lineHeight: isColumn ? '19px' : { xs: '17px', md: '22px' },
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(banner.title) }}
        />
        <Box
          sx={{
            ...promoBannerRichTextSx,
            fontWeight: 400,
            color: theme => theme.palette.common.white,
            fontSize: isColumn ? 12 : { xs: 12, md: '14px' },
            lineHeight: isColumn ? '16px' : { xs: '15px', md: '17px' },
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(banner.description) }}
        />
      </Stack>

      <Button
        variant="soft"
        href={banner.buttonUrl}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        endIcon={<MdChevronRight size={20} />}
        sx={{
          minHeight: isColumn ? '24px' : { xs: '19px', md: '32px' },
          borderRadius: { xs: '6px', md: '8px' },
          flexShrink: 0,
          alignSelf: isColumn ? 'flex-start' : { xs: 'center', sm: 'center' },
          color: '#fff !important',
          borderColor: theme => theme.palette.common.white,
          fontSize: isColumn ? '11px' : { xs: '9px', md: '12px' },
          fontWeight: 700,
          maxHeight: isColumn ? '24px' : { xs: '19px', md: '32px' },
          pl: 1,
          whiteSpace: 'nowrap',
          '& .MuiButton-endIcon': { ml: 0.1 },
          '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255, 255, 255, 0.14)' },
        }}
      >
        {banner.buttonLabel}
      </Button>
    </Box>
  );
};
